import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, GraduationCap, TrendingUp, Truck, ShieldCheck, LifeBuoy, Heart, ShoppingCart, PlayCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { useCart } from "../context/CartContext";
import api from "../lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";

const GOLD = "#B0894F";

const FEATURES = [
  { icon: BookOpen, label: "Digital Resources" },
  { icon: GraduationCap, label: "Expert Knowledge" },
  { icon: TrendingUp, label: "Real Results" },
];

// Maps mockup filter labels -> which product categories they include
const FILTERS = [
  { key: "all", label: "All Products" },
  { key: "Books", label: "Books" },
  { key: "Courses", label: "Courses" },
  { key: "Guides", label: "Guides" },
  { key: "Templates", label: "Templates" },
  { key: "Business Tools", label: "Business Tools" },
  { key: "Bundles", label: "Bundles" },
];

const CATEGORY_LABEL = {
  "E-Book": "BOOK",
  "Guides": "GUIDE",
  "Templates": "TEMPLATES",
  "Toolkits": "BUSINESS TOOLS",
  "Bundles": "BUNDLE",
  "Courses & Masterclasses": "COURSE",
};

// Which filter bucket a product falls into
function bucketOf(p) {
  const c = p.category;
  if (c === "E-Book") return "Books";
  if (c === "Guides") return "Guides";
  if (c === "Templates") return "Templates";
  if (c === "Toolkits") return "Business Tools";
  if (c === "Bundles") return "Bundles";
  return "Courses";
}

export default function Academy({ canineOnly = false }) {
  const [courses, setCourses] = useState([]);
  const [digital, setDigital] = useState([]);
  const [active, setActive] = useState("all");
  const [sort, setSort] = useState("featured");
  const { add } = useCart();
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsTrigger = useRef(null);
  const showDetails = (product, event) => {
    detailsTrigger.current = event.currentTarget;
    setDetailProduct(product);
    setDetailsOpen(true);
  };

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/digital-products").then((r) => setDigital(r.data)).catch(() => {});
  }, []);

  const addToCart = (p) => {
    add({ id: p.id, name: p.name, price: p.price, image: p.image });
    toast.success(`${p.name} added to cart`, { description: "Go to your cart to check out." });
  };

  // Unified item list: digital products (buyable) + courses (viewable)
  const items = useMemo(() => {
    const digi = digital.filter((p) => !canineOnly || ["dig_ebook_boerboel_bible", "dig_complete_owners_package", "dig_wb_boerboel_workbook", "dig_wb_health_nutrition", "dig_wb_breeding_genetics"].includes(p.id)).map((p) => ({ ...p, _kind: "digital", _bucket: bucketOf(p) }));
    const crs = courses.filter((c) => !canineOnly || ["course_ownership-foundations", "course_puppy-prep-nutrition", "course_structure-health-breeding"].includes(c.id)).map((c) => ({
      id: c.id, name: c.title, description: c.summary, image: c.image,
      category: "Courses & Masterclasses", price: null, _kind: "course", _bucket: "Courses", _to: `/academy/${c.id}`,
    }));
    // Flagship first, then courses, then the rest
    const flagshipOrder = ["dig_ebook_boerboel_bible", "dig_complete_owners_package"];
    let list = [...digi, ...crs];
    if (sort === "price_low") list = [...list].sort((a, b) => (a.price ?? 1e9) - (b.price ?? 1e9));
    else if (sort === "price_high") list = [...list].sort((a, b) => (b.price ?? -1) - (a.price ?? -1));
    else list = [...list].sort((a, b) => {
      const ai = flagshipOrder.indexOf(a.id), bi = flagshipOrder.indexOf(b.id);
      if (ai !== -1 || bi !== -1) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      return 0;
    });
    return active === "all" ? list : list.filter((p) => p._bucket === active);
  }, [digital, courses, active, sort, canineOnly]);

  return (
    <Layout>
      {/* STORE HERO */}
      <section className="bg-white border-b border-eon-border">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 pt-14 lg:pt-20 pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 items-end">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] mb-4" style={{ color: GOLD }}>{canineOnly ? "EON Boerboels Academy" : "E.O.N. Infinity Digital Store"}</div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.03] text-eon-text tracking-tight">
                {canineOnly ? "Knowledge for Boerboel Owners." : "Resources for Learning and Business."}
              </h1>
              <p className="mt-6 text-base text-eon-muted max-w-2xl leading-relaxed">
                {canineOnly ? "Explore existing Boerboel books and courses on ownership, health, nutrition and breeding." : "Browse our complete catalog of books, courses, templates and business systems."}
              </p>
              <Link className="inline-block mt-5 underline font-semibold" to={canineOnly ? "/academy" : "/boerboels/academy"}>{canineOnly ? "Browse all corporate digital resources" : "Visit the Boerboels books and courses collection"}</Link>
            </div>
            <div className="lg:border-l lg:border-eon-border lg:pl-10">
              <div className="grid grid-cols-3 gap-4">
                {FEATURES.map((f) => (
                  <div key={f.label} className="flex flex-col items-center text-center gap-2">
                    <f.icon size={26} className="text-eon-text" strokeWidth={1.6} />
                    <span className="text-xs font-semibold text-eon-text leading-tight">{f.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 text-center text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: GOLD }}>
                Education Today. A Stronger Tomorrow.
                <div className="text-eon-muted font-semibold tracking-[0.2em] mt-1">— E.O.N Infinity Inc</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FILTER BAR */}
      <div className="bg-white sticky top-0 z-20 border-b border-eon-border">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-4 flex items-center gap-3 overflow-x-auto">
          <div className="flex items-center gap-2 flex-1">
            {FILTERS.filter((f) => !canineOnly || !["Templates", "Business Tools"].includes(f.label)).map((f) => (
              <button key={f.key} onClick={() => setActive(f.key)} data-testid={`store-filter-${f.key}`}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors border ${active === f.key ? "bg-eon-text text-white border-eon-text" : "bg-white text-eon-text border-eon-border hover:border-eon-text"}`}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-eon-muted whitespace-nowrap">Sort by</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="store-sort"
              className="rounded-full border border-eon-border bg-white text-sm font-semibold px-3 py-2 outline-none">
              <option value="featured">Featured</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* PRODUCT GRID */}
      <section id="store-products" className="bg-eon-surface scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10 lg:py-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {items.map((p) => {
              const isBundle = p.category === "Bundles";
              const save = p.compare_at_price && p.price ? Math.round(p.compare_at_price - p.price) : 0;
              return (
                <div key={p.id} data-testid={`store-card-${p.id}`}
                  className="group bg-white rounded-2xl border border-eon-border overflow-hidden flex flex-col hover:shadow-xl transition-shadow">
                  <div className="relative aspect-square overflow-hidden bg-eon-surface">
                    {p._kind === "digital" && <button type="button" onClick={(event) => showDetails(p, event)} aria-label={`View details for ${p.name}`} data-testid={`store-cover-${p.id}`} className="absolute inset-0 z-10 focus-visible:outline focus-visible:outline-4 focus-visible:outline-blue-700 focus-visible:outline-offset-[-4px]" />}
                    {p._kind === "course" && <Link to={p._to} aria-label={`View course ${p.name}`} className="absolute inset-0 z-10 focus-visible:outline focus-visible:outline-4 focus-visible:outline-blue-700 focus-visible:outline-offset-[-4px]" />}
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    {p._kind === "course" && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="w-12 h-12 rounded-full bg-white/85 flex items-center justify-center shadow-md"><PlayCircle size={26} className="text-eon-text" /></span>
                      </span>
                    )}
                    {isBundle && (
                      <span className="absolute top-3 right-3 text-[10px] font-black uppercase tracking-wider rounded-full px-3 py-2 text-white shadow" style={{ backgroundColor: GOLD }}>Best Value</span>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-eon-muted">{CATEGORY_LABEL[p.category] || "PRODUCT"}</div>
                    <h3 className="font-display text-lg font-bold text-eon-text mt-1 leading-snug">
                      {p._kind === "digital" ? <button type="button" onClick={(event) => showDetails(p, event)} data-testid={`store-title-${p.id}`} className="text-left hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-700">{p.name}</button> : <Link to={p._to} className="hover:underline">{p.name}</Link>}
                    </h3>
                    <p className="text-sm text-eon-muted mt-2 line-clamp-3 flex-1">{p.description}</p>
                    {p._kind === "digital" && <button type="button" onClick={(event) => showDetails(p, event)} data-testid={`store-details-${p.id}`} aria-label={`View details for ${p.name}`} className="self-start min-h-[44px] mt-2 text-sm font-semibold text-blue-800 underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-700">View Details</button>}
                    <div className="flex items-center justify-between mt-4 gap-2">
                      <div className="flex items-baseline gap-2">
                        {p.price != null ? (
                          <>
                            <span className="font-display text-2xl font-black text-eon-text">${p.price}</span>
                            {p.compare_at_price && <span className="text-sm text-eon-muted line-through">${p.compare_at_price}</span>}
                          </>
                        ) : <span className="font-display text-sm font-bold" style={{ color: GOLD }}>Included</span>}
                        {save > 0 && <span className="text-[10px] font-black uppercase text-white rounded-full px-2 py-1" style={{ backgroundColor: GOLD }}>Save ${save}</span>}
                      </div>
                      {p._kind === "course" ? (
                        <Link to={p._to} data-testid={`store-view-${p.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full bg-eon-text text-white text-xs font-bold px-4 py-2.5 hover:bg-black transition-colors whitespace-nowrap">
                          View Course <ArrowRight size={14} />
                        </Link>
                      ) : (
                        <button onClick={() => addToCart(p)} data-testid={`store-add-${p.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full bg-eon-text text-white text-xs font-bold px-4 py-2.5 hover:bg-black transition-colors whitespace-nowrap">
                          Add to Cart <ShoppingCart size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {items.length === 0 && (
            <div className="text-center py-20 text-eon-muted">More in this category is coming soon.</div>
          )}
        </div>
      </section>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent data-testid="product-details-dialog" className="[&>button]:h-11 [&>button]:w-11 [&>button]:inline-flex [&>button]:items-center [&>button]:justify-center [&>button]:right-2 [&>button]:top-2 z-[70] w-[calc(100%-2rem)] max-w-2xl max-h-[85dvh] overflow-y-auto rounded-2xl bg-white text-slate-900 p-6 sm:p-8" onCloseAutoFocus={(event) => { event.preventDefault(); if (detailsTrigger.current?.isConnected) detailsTrigger.current.focus(); }}>
          {detailProduct && <>
            <DialogTitle className="pr-8 text-2xl font-bold leading-tight">{detailProduct.name}</DialogTitle>
            <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-6 items-start">
              <img src={detailProduct.image} alt={detailProduct.name} className="w-40 rounded-lg mx-auto" />
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-600">{detailProduct.category}</div>
                <DialogDescription className="mt-3 whitespace-pre-line text-base leading-relaxed text-slate-700">{detailProduct.description}</DialogDescription>
                <p className="mt-5 text-2xl font-bold" data-testid="product-details-price">${detailProduct.price}</p>
                <button type="button" onClick={() => addToCart(detailProduct)} data-testid="product-details-add" className="mt-4 min-h-[44px] rounded-full bg-slate-900 px-6 py-3 font-bold text-white hover:bg-blue-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700">Add to Cart</button>
              </div>
            </div>
          </>}
        </DialogContent>
      </Dialog>

      {/* STORE INFORMATION */}
      <section aria-label="Store information" className="bg-white border-t border-eon-border">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <details data-testid="store-access-info" className="rounded-lg border border-eon-border p-4"><summary className="min-h-[44px] cursor-pointer font-bold text-sm text-eon-text">Digital access — how it works</summary><p className="mt-3 text-sm text-eon-muted leading-relaxed">After payment is confirmed, available download links appear on the payment confirmation page. If a link is missing or you need help opening your purchase, <Link to="/contact" className="underline text-blue-800">contact customer support</Link>.</p></details>
            <details data-testid="store-checkout-info" className="rounded-lg border border-eon-border p-4"><summary className="min-h-[44px] cursor-pointer font-bold text-sm text-eon-text">Checkout with Stripe — details</summary><p className="mt-3 text-sm text-eon-muted leading-relaxed">Add your chosen item to the cart, review the total, and use the checkout button to continue to Stripe. Opening these details does not start a payment.</p><Link to="/cart" className="mt-3 inline-flex min-h-[44px] items-center underline text-blue-800">Review your cart</Link></details>
            <div className="rounded-lg border border-eon-border p-4"><Link to="/contact" data-testid="store-customer-support" className="inline-flex min-h-[44px] items-center gap-2 font-bold text-sm text-blue-800 underline">Customer Support <ArrowRight size={16}/></Link><p className="mt-3 text-sm text-eon-muted">Questions about a product or purchase? Contact E.O.N.</p></div>
            <details data-testid="store-purpose-info" className="rounded-lg border border-eon-border p-4"><summary className="min-h-[44px] cursor-pointer font-bold text-sm text-eon-text">Built for a Greater Purpose — learn more</summary><p className="mt-3 text-sm text-eon-muted leading-relaxed">Education. Better Dogs. Stronger Communities. Explore the Boerboels resources behind this store.</p><Link to="/boerboels" className="mt-3 inline-flex min-h-[44px] items-center underline text-blue-800">Explore E.O.N. Boerboels</Link></details>
          </div>
          <button onClick={() => { setActive("all"); document.getElementById("store-products")?.scrollIntoView({ behavior: "auto", block: "start" }); }} data-testid="store-explore-all" className="mt-6 rounded-full bg-slate-900 text-white text-sm font-bold px-7 py-3.5 min-h-[44px]">Explore All Products →</button>
        </div>
      </section>
    </Layout>
  );
}